/**
 * The annotated product: the Leader Rule, made into a block.
 *
 * One studio shot with notes pinned to real places on the body. Each note is
 * an index, a title, a sentence or two, and a 1px leader that runs from the
 * text back to a dot on the object. That leader is the only decoration this
 * design system allows itself, and it is what makes "how it was built" read as
 * annotation rather than as a timeline.
 *
 * ## Where a note points
 *
 * `x` and `y` are fractions of the *shot*, not pixels: 0,0 is its top-left and
 * 1,1 its bottom-right. Stored that way so an editor can swap the render for a
 * new angle at a different size and every dot stays on the part it named. They
 * are also the only two numbers an editor has to understand.
 *
 * ## Why the leader is drawn rather than styled
 *
 * The dot is over the image and the text is out in the page margin. Nothing in
 * CSS connects two boxes in different stacking contexts, so an SVG spans the
 * whole stage and the path is measured from the live boxes after layout — on
 * resize, on font load, and when a lazy image finally reports its height.
 *
 * ## Below 62rem
 *
 * There is no margin to write in, so the leaders are dropped and the notes
 * become a numbered list under the shot — with the same numbers still printed
 * on the body, so the two halves still refer to each other. That is a
 * different layout for the same information, which DESIGN.md names as the one
 * place this page has a real breakpoint.
 */

import type { BlockRenderer } from '@remidus/web-core'

import { claimAnchor } from '../anchors'
import { paragraphs, plinth, resolveAll } from './prose'

interface Note {
  /** Fraction of the shot's width, 0–1. */
  x?: number
  /** Fraction of the shot's height, 0–1. */
  y?: number
  /** Which margin the text sits in. Defaults by which half of the shot the
   *  dot is on, which is right far more often than not. */
  side?: 'left' | 'right'
  title?: string
  text?: string
}

const SVG = 'http://www.w3.org/2000/svg'
const clamp01 = (n: unknown): number => Math.min(1, Math.max(0, Number(n) || 0))

/**
 * The element whose box *is* the picture.
 *
 * `x`/`y` are fractions of the shot, and the figure is taller than the shot by
 * exactly its caption — so hanging the dots on the figure put every one of them
 * about a caption's worth too low, consistently enough to look deliberate and
 * wrong. Core wraps the image in `.rem-media__picture`, which is the box the
 * fractions actually mean.
 */
function pictureBox(shot: HTMLElement): HTMLElement {
  const picture = shot.querySelector<HTMLElement>('.rem-media__picture')
  if (picture) return picture
  const img = shot.querySelector<HTMLElement>('img')
  return img?.parentElement ?? shot
}

export const renderAnatomy: BlockRenderer = (block, ctx) => {
  const data = (block.data ?? {}) as {
    kicker?: string
    heading?: string
    caption?: string
    media_id?: string
    media_ids?: string[] | string
    notes?: Note[]
  }
  const notes = (data.notes ?? []).filter((n) => n?.title)

  const stage = document.createElement('section')
  stage.className = 'skin-stage skin-stage--anatomy'
  claimAnchor(stage, data.heading ?? 'The body', data.kicker)

  if (data.kicker) {
    const kicker = document.createElement('p')
    kicker.className = 'skin-kicker'
    kicker.textContent = data.kicker
    kicker.dataset.remField = 'kicker'
    stage.append(kicker)
  }
  if (data.heading) {
    const heading = document.createElement('h2')
    heading.className = 'skin-stage__title'
    heading.textContent = data.heading
    heading.dataset.remField = 'heading'
    stage.append(heading)
  }
  if (data.caption) {
    const caption = document.createElement('p')
    caption.className = 'skin-stage__lede'
    caption.textContent = data.caption
    caption.dataset.remField = 'caption'
    caption.setAttribute('data-rem-field-multiline', '')
    stage.append(caption)
  }

  const field = document.createElement('div')
  field.className = 'skin-anatomy'

  const shot = plinth(resolveAll(data, ctx)[0], 'skin-plinth--anatomy')
  const canvas = shot ? pictureBox(shot) : null
  if (!shot) {
    // No render yet is a legitimate state — an editor may add the block before
    // uploading the shot — and the notes are still worth reading.
    field.classList.add('skin-anatomy--bare')
  } else {
    field.append(shot)
  }

  // One SVG across the whole field. `preserveAspectRatio: none` is deliberate:
  // the viewBox is set to the field's own pixel size on every layout, so the
  // path is authored in the same units it is measured in.
  const lines = document.createElementNS(SVG, 'svg')
  lines.setAttribute('class', 'skin-anatomy__leaders')
  lines.setAttribute('aria-hidden', 'true')
  lines.setAttribute('preserveAspectRatio', 'none')
  field.append(lines)

  const list = document.createElement('ol')
  list.className = 'skin-anatomy__notes'

  const dots: HTMLElement[] = []
  const chips: HTMLElement[] = []

  for (const [index, note] of notes.entries()) {
    const x = clamp01(note.x)
    const y = clamp01(note.y)
    const side = note.side === 'left' || note.side === 'right' ? note.side : x < 0.5 ? 'left' : 'right'
    const number = String(index + 1).padStart(2, '0')

    if (canvas) {
      const dot = document.createElement('span')
      dot.className = 'skin-anatomy__dot'
      dot.dataset.index = number
      dot.style.left = `${x * 100}%`
      dot.style.top = `${y * 100}%`
      dot.setAttribute('aria-hidden', 'true')
      canvas.append(dot)
      dots.push(dot)
    }

    const chip = document.createElement('li')
    chip.className = 'skin-anatomy__note'
    chip.dataset.side = side
    chip.dataset.remItem = String(index)
    // Where the note wants to sit, so the wide layout can put it level with
    // the part it names.
    chip.dataset.y = String(y)

    const label = document.createElement('span')
    label.className = 'skin-anatomy__index'
    label.textContent = number

    const body = document.createElement('div')
    body.className = 'skin-anatomy__body'
    const title = document.createElement('h3')
    title.className = 'skin-anatomy__note-title'
    title.textContent = note.title ?? ''
    title.dataset.remField = `notes.${index}.title`
    body.append(title)
    if (note.text) {
      const prose = document.createElement('div')
      prose.className = 'skin-anatomy__note-text'
      prose.dataset.remField = `notes.${index}.text`
      prose.setAttribute('data-rem-field-multiline', '')
      paragraphs(prose, note.text)
      body.append(prose)
    }

    chip.append(label, body)
    list.append(chip)
    chips.push(chip)
  }

  field.append(list)
  stage.append(field)

  if (canvas && chips.length) layout(field, canvas, lines, dots, chips)
  return stage
}

/**
 * Place the notes beside the parts they name, then draw one path per note.
 *
 * ## Why the notes are placed here and not by CSS
 *
 * A note belongs level with its own dot, and where that dot is depends on the
 * rendered height of the shot — a number CSS has, and cannot do arithmetic
 * with. Left in the normal flow they stack by list order instead, and by the
 * fourth note the leader is a three-hundred-pixel diagonal to something at the
 * top of the picture.
 *
 * Absolute placement brings back the problem flow was solving, so each margin
 * gets one downward pass: a note that would land on the one above it is pushed
 * clear. The order within a margin is preserved, nothing overlaps, and a note
 * only moves as far as it has to.
 *
 * ## Why the path is two segments
 *
 * A short horizontal stub off the edge of the text, then a straight run to the
 * dot. A single diagonal reads as a scribble at shallow angles; the stub gives
 * every leader the same start and makes a stack of them look drawn by one hand.
 */
function layout(
  field: HTMLElement,
  canvas: HTMLElement,
  svg: SVGSVGElement,
  dots: HTMLElement[],
  chips: HTMLElement[],
): void {
  const STUB = 14
  const GAP = 16

  const place = (frame: DOMRect): void => {
    const shot = canvas.getBoundingClientRect()
    const top = shot.top - frame.top
    let lowest = 0

    for (const side of ['left', 'right'] as const) {
      const column = chips.filter((chip) => chip.dataset.side === side)
      // By where they point, not by where they are written: the numbering is a
      // reading order and does not have to descend the object.
      column.sort((a, b) => Number(a.dataset.y) - Number(b.dataset.y))
      let floor = 0
      for (const chip of column) {
        const wanted = top + Number(chip.dataset.y) * shot.height - 18
        const at = Math.max(wanted, floor)
        chip.style.top = `${at}px`
        floor = at + chip.offsetHeight + GAP
        lowest = Math.max(lowest, floor)
      }
    }
    // The notes are out of flow, so nothing else knows how tall the stage has
    // to be. Without this the next section starts underneath the last note.
    field.style.minHeight = `${Math.max(lowest, shot.height + top)}px`
  }

  const draw = (): void => {
    const frame = field.getBoundingClientRect()
    if (frame.width === 0) return

    // Leaders are a wide-screen device. Below the breakpoint the notes sit
    // under the shot and a line to them would cross the whole picture.
    const wide = window.matchMedia('(min-width: 62rem)').matches
    svg.style.display = wide ? '' : 'none'
    if (!wide) {
      for (const chip of chips) chip.style.top = ''
      field.style.minHeight = ''
      return
    }

    place(frame)
    // Placement changes the field's own height, so every offset measured
    // against it has to be taken again.
    const placed = field.getBoundingClientRect()
    svg.setAttribute('viewBox', `0 0 ${placed.width} ${placed.height}`)
    svg.setAttribute('width', String(placed.width))
    svg.setAttribute('height', String(placed.height))

    svg.replaceChildren()
    for (const [i, dot] of dots.entries()) {
      const chip = chips[i]
      if (!chip) continue
      const d = dot.getBoundingClientRect()
      const c = chip.getBoundingClientRect()
      if (c.width === 0) continue

      const left = chip.dataset.side === 'left'
      const startX = (left ? c.right : c.left) - placed.left
      const startY = c.top - placed.top + Math.min(22, c.height / 2)
      const dotX = d.left - placed.left + d.width / 2
      const dotY = d.top - placed.top + d.height / 2
      const stubX = left ? startX + STUB : startX - STUB

      const path = document.createElementNS(SVG, 'path')
      path.setAttribute('d', `M ${startX} ${startY} H ${stubX} L ${dotX} ${dotY}`)
      path.setAttribute('class', 'skin-anatomy__leader')
      // The motion layer animates the draw by moving the dash offset, and
      // needs a length it can only get once the path is in the document.
      svg.append(path)
      const length = path.getTotalLength()
      path.style.setProperty('--leader-length', String(length))
      path.setAttribute('stroke-dasharray', String(length))
    }
    field.dispatchEvent(new CustomEvent('skin:leaders', { bubbles: true }))
  }

  // Fires on the shot loading, on resize, and on a font swap changing a chip's
  // height — all three move an endpoint, and none of them is a scroll event.
  // Observing the field alone would loop: `place` sets its min-height.
  const observer = new ResizeObserver(() => draw())
  observer.observe(canvas)
  for (const chip of chips) observer.observe(chip)
  for (const img of field.querySelectorAll('img')) {
    if (!img.complete) img.addEventListener('load', () => draw(), { once: true })
  }
  window.addEventListener('resize', draw)
  requestAnimationFrame(draw)
}
